import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { CountryCombobox } from "@/components/ui/CountryCombobox";
import { errorMessage } from "@/lib/api";
import { countryLabel } from "@/lib/employeeRecordLabels";
import { fillCitizenship, undoCitizenship, type CitizenshipBatchResult } from "@/lib/citizenshipBatch";

export function CitizenshipBatchPanel({ workerIds, onSaved, onBusy }: {
  workerIds: string[]; onSaved: () => void; onBusy: (busy: boolean) => void;
}) {
  const [country, setCountry] = useState("HR");
  const [confirm, setConfirm] = useState<{ ids: string[]; country: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  const [result, setResult] = useState<CitizenshipBatchResult | null>(null);
  const [undo, setUndo] = useState<CitizenshipBatchResult | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    if (!undo) return;
    const timer = window.setTimeout(() => setUndo(null), Math.max(0, Date.parse(undo.undo_expires_at) - Date.now()));
    return () => window.clearTimeout(timer);
  }, [undo]);
  async function save(isUndo: boolean) {
    if (sending.current || (isUndo ? !undo : !confirm)) return;
    sending.current = true; setBusy(true); onBusy(true); setError("");
    try {
      const value = isUndo ? await undoCitizenship(undo!.batch_id) : await fillCitizenship(confirm!.ids, confirm!.country);
      setResult(value); setMessage(`${isUndo ? "Poništeno" : "Upisano"}: ${value.updated_ids.length} radnika.`);
      setUndo(isUndo || !value.updated_ids.length ? null : value); setConfirm(null); onSaved();
    } catch (cause) { setError(errorMessage(cause, isUndo ? "Poništenje nije uspjelo." : "Upis državljanstva nije uspio.")); }
    finally { sending.current = false; setBusy(false); onBusy(false); }
  }
  return <section className="space-y-3 rounded-lg border p-3" aria-label="Skupni upis državljanstva">
    <p>Odabrano {workerIds.length} radnika bez upisanog državljanstva.</p>
    <Button className="min-h-11" disabled={busy || !workerIds.length || !country || !!undo} onClick={() => setConfirm({ ids: [...workerIds], country })}>Označi kao hrvatske državljane</Button>
    <details><summary className="min-h-11 cursor-pointer">Druga država (opcionalno)</summary>
      <CountryCombobox label="Državljanstvo za odabrane" value={country} onChange={setCountry} required disabled={busy || !!confirm || !!undo} />
    </details>
    {confirm && <div role="dialog" aria-label="Potvrda upisa državljanstva" className="space-y-3 border-t pt-3">
      <p>Upisat će se državljanstvo {countryLabel(confirm.country)} za {confirm.ids.length} radnika.</p>
      <p>Već upisana državljanstva bit će preskočena.</p>
      <Button className="min-h-11" disabled={busy} onClick={() => void save(false)}>Potvrdi upis</Button>{" "}
      <Button className="min-h-11" variant="secondary" disabled={busy} onClick={() => setConfirm(null)}>Odustani</Button>
    </div>}
    {error && <p role="alert">{error}</p>}
    {result && <div role="status"><p>{message}</p>{result.skipped.length > 0 && <><p>Preskočeni radnici:</p><ul>{result.skipped.map(row => <li key={row.worker_id}>{row.worker_name}: {row.reason}</li>)}</ul></>}</div>}
    {undo && <Button className="min-h-11" variant="secondary" disabled={busy} onClick={() => void save(true)}>Poništi</Button>}
  </section>;
}
